/* ------------------------------------------------------------------ *
 * الملف الشخصي — `Features/User/UserProfile` and `Features/User/ChangePassword`.
 *
 *   getProfilePage  -> GET  /api/portal/profile/           NEW — the page payload
 *   updateProfile   -> POST /UserProfile/Update
 *   getPasswordPage -> GET  /api/portal/change-password/   NEW
 *   changePassword  -> POST /ChangePassword/Submit
 *
 * Both siblings are new because both pages carried data no action reports: the profile page
 * holds ELEVEN pairs of visible/editable flags, the five custom attribute labels and the
 * department list, and the password page holds `IsLocalUser`. API_FINDINGS.md §79.
 * ------------------------------------------------------------------ */

import { api } from '../../../lib/api'
import { isEnglish } from '../../../lib/i18n'

const PROFILE = '/UserProfile'
const PASSWORD = '/ChangePassword'
const PROFILE_PAGE = '/api/portal/profile'
const PASSWORD_PAGE = '/api/portal/change-password'

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/**
 * The profile, the departments it may claim, and WHICH FIELDS EXIST.
 *
 * The eleven `IsXVisible` / `IsXEditable` pairs are the point of this payload. A client decides
 * per field whether their people may see it and whether they may change it — so a screen that
 * rendered all fifteen inputs regardless would be offering edits the server will discard and
 * showing data the client chose to hide. Every field below carries its own two flags.
 */
export async function getProfilePage(signal) {
  const dto = (await api.get(`${PROFILE_PAGE}/`, { signal })) ?? {}
  const profile = dto.Profile ?? {}

  const field = (name) => ({
    visible: Boolean(dto[`Is${name}Visible`]),
    editable: Boolean(dto[`Is${name}Editable`]),
  })

  /* The five free-text attributes a client names for itself — "Manager Name" here. A label that
     is still the default `Attribute3` means nobody configured it; it is shown as stored either
     way (W6), including when the stored value is damaged. */
  const attributes = [1, 2, 3, 4, 5].map((index) => ({
    key: `attribute${index}`,
    label: label(dto[`Attribute${index}ArName`], dto[`Attribute${index}EnName`]),
    value: profile[`Attribute${index}`] ?? '',
    ...field(`Attribute${index}`),
  }))

  return {
    userId: profile.UserId,
    username: profile.UserName ?? '',
    nameAr: profile.ArabicFullName ?? '',
    nameEn: profile.EnglishFullName ?? '',
    /* 1 male, 2 female — the two the legacy select offers. Null on a profile that never said. */
    gender: profile.Gender ?? null,
    email: profile.Email ?? '',
    phone: profile.Phone ?? '',
    mobile: profile.Mobile ?? '',
    employeeId: profile.EmployeeId ?? '',
    departmentId: profile.DepartmentId ?? 0,
    /* `TypeId == 1` groups for this company, plus the company-less ones. EMPTY IF THE QUERY IS
       ASKED WITHOUT A CompanyId — which is how the department picker ends up with no options. */
    departments: (dto.Groups ?? []).map((group) => ({
      id: group.GroupId,
      name: label(group.ArabicName, group.EnglishName),
    })),
    fields: {
      username: field('UserName'),
      nameEn: field('EnglishName'),
      nameAr: field('ArabicName'),
      gender: field('Gender'),
      email: field('Email'),
      phone: field('Phone'),
      mobile: field('Mobile'),
      employeeId: field('EmployeeId'),
      departmentId: field('DepartmentId'),
    },
    attributes,
  }
}

/**
 * Save the profile.
 *
 * The body is camelCase and so is the answer — `{status, message, controller}` — unlike the
 * password route below it, which is PascalCase both ways.
 *
 * `controller` is a REDIRECT the server may ask for after a successful save; the legacy obeys it
 * with `location.href`. It is passed back to the caller rather than acted on here.
 */
export async function updateProfile(form, signal) {
  const data = await api.post(
    `${PROFILE}/Update`,
    {
      nameEnglish: form.nameEn,
      nameArabic: form.nameAr,
      gender: form.gender == null || form.gender === '' ? null : Number(form.gender),
      email: form.email,
      phone: form.phone,
      mobile: form.mobile,
      employeeId: form.employeeId,
      groupId: Number(form.departmentId) || 0,
      attribute1: form.attribute1 ?? '',
      attribute2: form.attribute2 ?? '',
      attribute3: form.attribute3 ?? '',
      attribute4: form.attribute4 ?? '',
      attribute5: form.attribute5 ?? '',
    },
    { signal },
  )

  return {
    ok: Boolean(data?.status),
    message: data?.message ?? '',
    redirectTo: data?.controller || null,
  }
}

/**
 * Whether this account HAS a password here.
 *
 * An account that signs in through an external provider does not, and the form says so instead of
 * taking a password the server would refuse.
 */
export async function getPasswordPage(signal) {
  const dto = (await api.get(`${PASSWORD_PAGE}/`, { signal })) ?? {}
  return { isLocal: Boolean(dto.IsLocalUser) }
}

/**
 * Change it. The field is `password`, NOT `newPassword` — `ChangePasswordRequest` declares
 * `Password` and the command it maps to declares `NewPassword`, so the name the wire uses is the
 * request's, not the command's.
 */
export async function changePassword(currentPassword, newPassword, signal) {
  const data = await api.post(
    `${PASSWORD}/Submit`,
    { currentPassword, password: newPassword },
    { signal },
  )

  /* PascalCase here, camelCase on `/UserProfile/Update` — two slices, two conventions. */
  return { ok: Boolean(data?.Result), message: data?.Message ?? '' }
}
